import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';

import { effectiveSort, labelChoices, useCatalogFilters } from '@/entities/catalog';
import { roleLabelKey, useBoardLabelCatalog } from '@/entities/collab';
import { CREATE_DOCUMENT } from '@/entities/document';
import { type Section as SectionRow, useSections } from '@/entities/section';
import { useGroups } from '@/entities/workspace-group';
import { useShortcutBindings, useViewer } from '@/entities/viewer';
import { canCreateTasks, useWorkspace } from '@/entities/workspace';
import { AdvancedCreateDialog } from '@/features/advanced-create';
import { CreateBoardDialog } from '@/features/board-manage';
import { CatalogToolbar } from '@/features/catalog-filters';
import { CREATE_DATABASE, CreateDatabaseDialog } from '@/features/database-catalog';
import {
  CreateMenu,
  type CreateItem,
  ImportChooser,
  type ImportChoice,
} from '@/features/create-menu';
import { CreateDocumentDialog, ImportDocumentDialog } from '@/features/document-catalog';
import { GroupNameDialog } from '@/features/workspace-groups';
import { WorkspaceAdmin, WorkspaceTitle } from '@/features/workspace-admin';
import {
  canStyleWorkspace,
  WorkspaceAppearanceButton,
  WorkspaceBanner,
  WorkspaceInfo,
} from '@/features/workspace-header';
import { WorkspaceLabels } from '@/features/workspace-labels';
import { ImportBoardDialog } from '@/features/board-portability';
import { SectionNameDialog } from '@/features/workspace-sections';
import { ImportDatabaseDialog } from '@/features/database-portability';
import { WorkspaceTrashDialog } from '@/features/board-recovery';
import { QuickCreateDialog } from '@/features/quick-create';
import { originStateFrom } from '@/shared/lib/origin';
import { can, PERMISSIONS } from '@/shared/lib/permissions';
import { useShortcutHandler } from '@/shared/lib/shortcuts';
import { bindingText } from '@/shared/lib/shortcuts';
import { ErrorState, IconButton, Icons, LoadingState, SplitButton } from '@/shared/ui';

import styles from './WorkspacePage.module.css';
import { WorkspaceObjects } from './WorkspaceObjects';
import { WORKSPACE_OFFERED, WORKSPACE_SORTS } from './workspace-filters';

/**
 * Workspace = a catalog of boards and databases. One primary action (Create), quiet secondary
 * actions and icon links; creation forms are dialogs. The three catalog sections share one search
 * and one set of filters (the same ones as the home dashboard).
 */
export function WorkspacePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { workspaceId = '' } = useParams();
  const workspace = useWorkspace(workspaceId);
  const viewer = useViewer();
  const bindings = useShortcutBindings();
  const newTaskKeys = bindingText(bindings['new-task']);
  const createTitle = newTaskKeys
    ? `${t('quickCreate.open')} (${newTaskKeys})`
    : t('quickCreate.open');
  const [creatingBoard, setCreatingBoard] = useState(false);
  const [creatingDatabase, setCreatingDatabase] = useState(false);
  const [importingDatabase, setImportingDatabase] = useState(false);
  const [creating, setCreating] = useState(false);
  const [creatingAdvanced, setCreatingAdvanced] = useState(false);
  const [trashOpen, setTrashOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [choosingImport, setChoosingImport] = useState(false);
  const [search, setSearch] = useState('');
  const [creatingSection, setCreatingSection] = useState(false);
  const [creatingDocument, setCreatingDocument] = useState(false);
  const [importingDocument, setImportingDocument] = useState(false);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { filters, update, reset } = useCatalogFilters();
  const sectionList = useSections(workspaceId);
  // The open section (breadcrumb view) is part of the address, so a reload or a shared link keeps it.
  const groupList = useGroups(workspaceId);
  const labelCatalog = useBoardLabelCatalog(workspaceId, workspace.data?.workspace_type === 'team');
  const folderParam = searchParams.get('folder');
  const folder: SectionRow | null =
    sectionList.data?.items.find((s) => s.id === folderParam) ?? null;
  const groupParam = searchParams.get('group');
  const openGroup = groupList.data?.items.find((g) => g.id === groupParam) ?? null;
  const focus = (id: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('folder', id);
    else next.delete('folder');
    next.delete('group');
    setSearch(''); // opening a section leaves the search results
    setSearchParams(next, { replace: false });
  };
  /** The open group is part of the address too (a reload or a shared link keeps it). */
  const openGroupAt = (id: string | null, sectionId?: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('group', id);
    else next.delete('group');
    if (sectionId) next.set('folder', sectionId);
    else next.delete('folder');
    setSearch('');
    setSearchParams(next, { replace: false });
  };

  const mayCreate = canCreateTasks(workspace.data);
  useShortcutHandler(
    'new-task',
    mayCreate
      ? () => {
          setCreating(true);
        }
      : null,
  );

  if (workspace.isPending) return <LoadingState />;
  if (workspace.isError) return <ErrorState error={workspace.error} />;
  const canCreate = can(workspace.data.permissions, PERMISSIONS.workspaceCreateBoard);
  const canCreateTask = canCreateTasks(workspace.data);

  const timezone = viewer.status === 'authenticated' ? viewer.user.timezone : null;
  const userId = viewer.status === 'authenticated' ? viewer.user.id : null;
  const isTeam = workspace.data.workspace_type === 'team';
  // A notification may point straight at a management tab (``?tab=requests``).
  const requestedTab = searchParams.get('tab');
  const sections = isTeam
    ? (['all', 'boards', 'databases', 'documents', 'labels', 'manage'] as const)
    : (['all', 'boards', 'databases', 'documents'] as const);
  type Section = (typeof sections)[number];
  // Labels and administration are header tools (below), not part of the resource navigation.
  const resourceTabs = sections.filter(
    (value): value is Exclude<Section, 'labels' | 'manage'> =>
      value !== 'labels' && value !== 'manage',
  );
  const toolLink = (value: 'labels' | 'manage') => {
    const next = new URLSearchParams();
    next.set('section', value);
    return `?${next.toString()}`;
  };
  const requested = searchParams.get('section');
  // "All" (boards and databases together) is the default; Boards / Databases narrow it by type.
  const section: Section =
    requested === 'boards' || requested === 'databases' || requested === 'documents'
      ? requested
      : !isTeam
        ? 'all'
        : requested === 'labels' || requestedTab === 'labels'
          ? 'labels'
          : requested === 'manage' || requestedTab !== null
            ? 'manage'
            : 'all';
  const kinds =
    section === 'boards'
      ? (['board'] as const)
      : section === 'databases'
        ? (['database'] as const)
        : section === 'documents'
          ? (['document'] as const)
          : (['board', 'database', 'document'] as const);
  const mayCreateDatabase = can(workspace.data.permissions, CREATE_DATABASE);
  const createItems: CreateItem[] = [];
  if (sectionList.data?.can_manage) {
    createItems.push({
      id: 'section',
      label: folder ? t('sections.newSubsection') : t('sections.newSection'),
      icon: <Icons.folder />,
      onSelect: () => {
        setCreatingSection(true);
      },
    });
  }
  if (canCreate) {
    createItems.push({
      id: 'board',
      label: t('workspace.newBoard'),
      icon: <Icons.board />,
      onSelect: () => {
        setCreatingBoard(true);
      },
    });
  }
  if (mayCreateDatabase) {
    createItems.push({
      id: 'database',
      label: t('databases.new'),
      icon: <Icons.layers />,
      onSelect: () => {
        setCreatingDatabase(true);
      },
    });
  }

  if (can(workspace.data.permissions, CREATE_DOCUMENT)) {
    createItems.push({
      id: 'document',
      label: t('documents.new'),
      icon: <Icons.document />,
      onSelect: () => {
        setCreatingDocument(true);
      },
    });
  }
  const importChoices: ImportChoice[] = [];
  if (canCreate) {
    importChoices.push({
      id: 'board',
      title: t('import.board.title'),
      hint: t('import.board.hint'),
      icon: <Icons.board />,
      onSelect: () => {
        setImportOpen(true);
      },
    });
  }
  if (mayCreateDatabase) {
    importChoices.push({
      id: 'database',
      title: t('import.database.title'),
      hint: t('import.database.hint'),
      icon: <Icons.layers />,
      onSelect: () => {
        setImportingDatabase(true);
      },
    });
  }
  if (can(workspace.data.permissions, CREATE_DOCUMENT)) {
    importChoices.push({
      id: 'document',
      title: t('import.document.title'),
      hint: t('import.document.hint'),
      icon: <Icons.document />,
      onSelect: () => {
        setImportingDocument(true);
      },
    });
  }
  if (importChoices.length > 0) {
    createItems.push({
      id: 'import',
      label: t('import.menu'),
      icon: <Icons.import />,
      separated: true,
      onSelect: () => {
        setChoosingImport(true);
      },
    });
  }
  if (groupList.data?.can_manage) {
    createItems.push({
      id: 'group',
      label: openGroup ? t('groups.newNested') : t('groups.new'),
      icon: <Icons.group />,
      onSelect: () => {
        setCreatingGroup(true);
      },
    });
  }

  return (
    <div className={styles.page}>
      <nav className={styles.crumbs} aria-label={t('workspace.toHomeHint')}>
        <Link to="/app" className={styles.homeLink} title={t('workspace.toHomeHint')}>
          <Icons.chevronLeft /> {t('workspace.toHome')}
        </Link>
      </nav>
      <WorkspaceBanner
        title={<WorkspaceTitle workspace={workspace.data} />}
        chips={
          isTeam
            ? [
                t('home.scopeTeam'),
                t(
                  workspace.data.custom_role_id
                    ? 'roles.custom'
                    : roleLabelKey(workspace.data.role),
                ),
              ]
            : [t('home.scopePersonal')]
        }
        appearance={workspace.data.appearance}
        imageUrl={workspace.data.header_image_url ?? null}
        legacyColor={workspace.data.color}
        primary={
          <>
            <CreateMenu items={createItems} />
            {canCreateTask && (
              <SplitButton
                tone="quiet"
                label={t('quickCreate.open')}
                title={createTitle}
                onMain={() => {
                  setCreating(true);
                }}
                menuLabel={t('boardHeader.createMenu')}
                items={[
                  {
                    label: t('boardHeader.quick'),
                    icon: <Icons.plus />,
                    onSelect: () => {
                      setCreating(true);
                    },
                  },
                  {
                    label: t('boardHeader.advanced'),
                    icon: <Icons.edit />,
                    onSelect: () => {
                      setCreatingAdvanced(true);
                    },
                  },
                ]}
              />
            )}
          </>
        }
        tools={
          <>
            {canStyleWorkspace(workspace.data) && (
              <WorkspaceAppearanceButton workspace={workspace.data} />
            )}
            <Link
              to={`/app/workspaces/${workspaceId}/calendar`}
              className={styles.iconLink}
              aria-label={t('calendar.open')}
              title={t('workspace.openCalendar')}
            >
              <Icons.calendar />
            </Link>
            {isTeam && (
              <Link
                to={toolLink('labels')}
                className={styles.iconLink}
                aria-label={t('workspace.tools.labels')}
                title={t('workspace.tools.labels')}
                aria-current={section === 'labels' ? 'page' : undefined}
                data-active={section === 'labels' || undefined}
                data-testid="workspace-labels-link"
              >
                <Icons.tag />
              </Link>
            )}
            {isTeam && (
              <Link
                to={toolLink('manage')}
                className={styles.iconLink}
                aria-label={t('workspace.tools.admin')}
                title={t('workspace.tools.admin')}
                aria-current={section === 'manage' ? 'page' : undefined}
                data-active={section === 'manage' || undefined}
                data-testid="workspace-admin-link"
              >
                <Icons.sliders />
              </Link>
            )}
            {(can(workspace.data.permissions, PERMISSIONS.workspaceManageBoardTrash) ||
              can(workspace.data.permissions, CREATE_DOCUMENT)) && (
              <IconButton
                label={t('workspaceTrash.open')}
                icon={<Icons.trash />}
                onClick={() => {
                  setTrashOpen(true);
                }}
              />
            )}
          </>
        }
      >
        <WorkspaceInfo workspace={workspace.data} canManage={canStyleWorkspace(workspace.data)} />
      </WorkspaceBanner>

      <nav className={styles.sections} aria-label={t('workspaceAdmin.sections')}>
        {resourceTabs.map((value) => (
          <button
            key={value}
            type="button"
            className={`${styles.sectionTab} ${section === value ? styles.sectionTabOn : ''}`}
            aria-pressed={section === value}
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete('tab');
              if (value === 'all') next.delete('section');
              else next.set('section', value);
              setSearchParams(next, { replace: true });
            }}
          >
            {t(`workspace.nav.${value}`)}
          </button>
        ))}
      </nav>

      {isTeam && section === 'manage' && <WorkspaceAdmin workspace={workspace.data} />}
      {isTeam && section === 'labels' && <WorkspaceLabels workspace={workspace.data} />}
      {(section === 'all' ||
        section === 'boards' ||
        section === 'databases' ||
        section === 'documents') && (
        <section aria-labelledby="objects-title" className={styles.boards}>
          <h2 id="objects-title" className={styles.sectionTitle}>
            {t(`workspace.nav.${section}`)}
          </h2>
          <CatalogToolbar
            query={search}
            onQuery={setSearch}
            searchLabel={t(
              section === 'boards'
                ? 'boards.search'
                : section === 'databases'
                  ? 'databases.search'
                  : section === 'documents'
                    ? 'documents.search'
                    : 'workspace.objectsSearch',
            )}
            filters={filters}
            onChange={update}
            onReset={reset}
            labelChoices={labelChoices(labelCatalog.data ?? [], i18n.language)}
            offered={WORKSPACE_OFFERED}
            sorts={WORKSPACE_SORTS}
            sort={effectiveSort(filters, WORKSPACE_SORTS, 'manual')}
          />
          <WorkspaceObjects
            workspaceId={workspaceId}
            kinds={kinds}
            query={search}
            filters={filters}
            locale={i18n.language}
            userId={userId}
            focusId={folder?.id ?? null}
            onFocus={focus}
            groupId={groupParam}
            onOpenGroup={openGroupAt}
          />
        </section>
      )}

      {creatingBoard && (
        <CreateBoardDialog
          workspaceId={workspaceId}
          sectionId={openGroup ? openGroup.section_id : folder?.id}
          groupId={openGroup?.id}
          onCreated={(board) => {
            setCreatingBoard(false);
            void navigate(`/app/boards/${board.id}`, { state: originStateFrom(location) });
          }}
          onClose={() => {
            setCreatingBoard(false);
          }}
        />
      )}
      {creatingDatabase && (
        <CreateDatabaseDialog
          workspaces={[workspace.data]}
          workspaceId={workspaceId}
          sectionId={openGroup ? openGroup.section_id : folder?.id}
          groupId={openGroup?.id}
          onClose={() => {
            setCreatingDatabase(false);
          }}
          onCreated={(database) => {
            setCreatingDatabase(false);
            void navigate(`/app/databases/${database.id}`, { state: originStateFrom(location) });
          }}
        />
      )}
      {creatingDocument && (
        <CreateDocumentDialog
          workspaces={[workspace.data]}
          workspaceId={workspaceId}
          sectionId={openGroup ? openGroup.section_id : folder?.id}
          groupId={openGroup?.id}
          onClose={() => {
            setCreatingDocument(false);
          }}
          onCreated={(document) => {
            setCreatingDocument(false);
            void navigate(`/app/documents/${document.id}`, { state: originStateFrom(location) });
          }}
        />
      )}
      {importingDocument && (
        <ImportDocumentDialog
          workspaces={[workspace.data]}
          workspaceId={workspaceId}
          sectionId={openGroup ? openGroup.section_id : folder?.id}
          groupId={openGroup?.id}
          onClose={() => {
            setImportingDocument(false);
          }}
          onCreated={(document) => {
            setImportingDocument(false);
            void navigate(`/app/documents/${document.id}`, { state: originStateFrom(location) });
          }}
        />
      )}
      {creatingGroup && (
        <GroupNameDialog
          workspaceId={workspaceId}
          place={{
            section_id: openGroup ? openGroup.section_id : (folder?.id ?? null),
            parent_group_id: openGroup?.id ?? null,
          }}
          onClose={() => {
            setCreatingGroup(false);
          }}
        />
      )}
      {creatingSection && (
        <SectionNameDialog
          workspaceId={workspaceId}
          parent={folder}
          onClose={() => {
            setCreatingSection(false);
          }}
        />
      )}
      {importingDatabase && (
        <ImportDatabaseDialog
          workspaces={[workspace.data]}
          workspaceId={workspaceId}
          onClose={() => {
            setImportingDatabase(false);
          }}
          onImported={(database) => {
            setImportingDatabase(false);
            void navigate(`/app/databases/${database.id}`, { state: originStateFrom(location) });
          }}
        />
      )}
      {choosingImport && (
        <ImportChooser
          choices={importChoices}
          onClose={() => {
            setChoosingImport(false);
          }}
        />
      )}
      {importOpen && (
        <ImportBoardDialog
          workspaceId={workspaceId}
          onClose={() => {
            setImportOpen(false);
          }}
          onImported={(result) => {
            setImportOpen(false);
            void navigate(`/app/boards/${result.board_id}`, { state: originStateFrom(location) });
          }}
        />
      )}
      {trashOpen && (
        <WorkspaceTrashDialog
          workspaceId={workspaceId}
          boards={can(workspace.data.permissions, PERMISSIONS.workspaceManageBoardTrash)}
          onClose={() => {
            setTrashOpen(false);
          }}
        />
      )}
      {creatingAdvanced && (
        <AdvancedCreateDialog
          workspaceId={workspaceId}
          timezone={timezone}
          onClose={() => {
            setCreatingAdvanced(false);
          }}
        />
      )}
      {creating && (
        <QuickCreateDialog
          workspaceId={workspaceId}
          timezone={timezone}
          onClose={() => {
            setCreating(false);
          }}
        />
      )}
    </div>
  );
}
